/**
 * Scheme Configuration Interfaces
 * Used for creating and viewing detailed scheme configurations
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export enum SchemeBasedOn {
  REGION = 1,
  STATE = 2,
  CITY = 3,
  EXCEL_UPLOAD = 4,
}

export enum SchemeType {
  VALUE = 1,
  VOLUME = 2,
}

export enum TypeOfCalculation {
  CATEGORY = 1,
  SKU = 2,
}

export enum RewardType {
  PERCENTAGE = 1,
  MULTIPLIER = 2,
  FLAT_POINTS = 3,
  SPIN_WHEEL = 4,
  EGVS = 5,
  MERCHANDISE = 6,
}

export enum SchemeTypeVariant {
  SINGLE = 1,
  MULTIPLE = 2,
  CUMULATIVE = 3,
}

export enum TypeOfBenefit {
  REGULAR = 1,
  CASH = 2,
}

export enum SchemeTypeBasedOn {
  THRESHOLD = 1,
  SLAB_WISE = 2,
}

// ============================================================================
// PRODUCT & CATEGORY INTERFACES
// ============================================================================

export interface ProductCategory {
  id: number;
  category_name: string;
}

export interface ProductDetail {
  id: number;
  product_name: string;
  product_sku?: string;
}

export interface GetSchemeProductCategoryResponse {
  status: number;
  data: {
    product_details: ProductDetail[];
    ecommerce_categories: ProductCategory[];
  };
}

// ============================================================================
// SPIN WHEEL INTERFACES
// ============================================================================

export interface SpinWheel {
  id: number;
  name: string;
  config_type?: string;
}

export interface GetSpinWheelsResponse {
  status: number;
  data: SpinWheel[];
}

// ============================================================================
// REWARD PRODUCT INTERFACES
// ============================================================================

export interface DenominationPoint {
  denomination: number | string;
}

export interface SchemeRewardProduct {
  product_id: number;
  name: string;
  product_type?: number;
  denomination_points?: DenominationPoint[];
}

export interface GetRewardProductsResponse {
  status: number;
  data: SchemeRewardProduct[];
}

// ============================================================================
// EXCEL UPLOAD INTERFACES
// ============================================================================

export interface ExcelUploadResponse {
  status: number;
  body: {
    status: number;
    data: {
      approvedList: number[];
      rejectedList: any[];
      approved: number;
      rejected: number;
    };
  };
}

// ============================================================================
// SKU DETAILS & SLAB DETAILS
// ============================================================================

export interface SlabDetail {
  start_slab: number | string;
  end_slab: number | string;
  increased_by: number | string;
  denomination?: number | string;
}

export interface SkuDetail {
  sku_id: number | string;
  threshold_value?: number | string;
  increased_by?: number | string;
  denomination?: number | string;
  denomination_array?: DenominationPoint[];
  slab_details?: SlabDetail[];
  // For display purposes
  brand_name?: string;
  brand_category_name?: string;
}

// ============================================================================
// SCHEME CONFIGURATION (Full Scheme Data)
// ============================================================================

export interface SchemeConfiguration {
  id?: number;
  user_role_id: number | string;
  scheme_based_on: SchemeBasedOn | number | string;
  scheme_based_on_id?: string; // Comma-separated IDs
  start_date: string;
  end_date: string;
  calculation_based_on: SchemeType | number | string;
  type_of_calculation: TypeOfCalculation | number | string;
  scheme_banner?: string;
  trigger_notification: number | boolean;

  scheme_name: string;
  benefit_based_on: RewardType | number | string;
  scheme_type: SchemeTypeVariant | number | string;
  type_of_benefit: TypeOfBenefit | number | string;
  threshold_points_calculation: number | string;
  scheme_type_based_on: SchemeTypeBasedOn | number | string;
  spin_config_id?: number | string;

  threshold_value?: number | string;
  increased_by?: number | string;
  denomination?: number | string;
  denomination_array?: DenominationPoint[];

  // Targeting fields (only one should be used at a time)
  region?: string;
  state?: string;
  city?: string;

  scheme_short_desc: string;
  scheme_description: string;
  scheme_eligibility: string;
  terms_and_condtions: string; // Note: Legacy typo maintained

  sku_details: SkuDetail[];
  slab_details?: SlabDetail[];

  // Additional fields for API
  is_excel_import?: number;
  user_import?: number;
  user_details?: number[];
  scheme_based_on_ids?: number[];

  // Display fields (from API response)
  status?: string | number;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// API REQUEST/RESPONSE INTERFACES
// ============================================================================

export interface GetSchemeDetailsResponse {
  status: number;
  data: SchemeConfiguration[];
}

export interface CreateSchemeRequest {
  user_role_id: number | string;
  scheme_based_on: number | string;
  start_date: string;
  end_date: string;
  calculation_based_on: number | string;
  type_of_calculation: number | string;
  scheme_banner?: string;
  trigger_notification: number;

  scheme_name: string;
  benefit_based_on: number | string;
  scheme_type: number | string;
  type_of_benefit: number | string;
  threshold_points_calculation: number | string;
  scheme_type_based_on: number | string;
  spin_config_id?: number | string;

  threshold_value?: number | string;
  increased_by?: number | string;
  denomination?: number | string;

  scheme_short_desc: string;
  scheme_description: string;
  scheme_eligibility: string;
  terms_and_condtions: string;

  sku_details: SkuDetail[];
  slab_details?: SlabDetail[];

  is_excel_import: number;
  user_import: number;
  user_details: number[];
  scheme_based_on_ids: number[];
}

export interface CreateSchemeResponse {
  status: number;
  body: {
    status: number;
    data: {
      message: string;
      scheme_id?: number;
    };
  };
}

// ============================================================================
// FORM DATA INTERFACES
// ============================================================================

export interface SchemeConfigurationFormData {
  user_type: string;
  scheme_based_on: string;
  start_date: string;
  end_date: string;
  calculation_based_on: string;
  type_of_calculation: string;
  scheme_banner?: string;
  trigger_notification: boolean;

  scheme_name: string;
  benefit_based_on: string;
  scheme_type: string;
  type_of_benefit: string;
  threshold_points_calculation: string;
  scheme_type_based_on: string;
  spin_config_id?: string;

  threshold_value?: string;
  increased_by?: string;
  denomination?: string;
  denomination_array?: DenominationPoint[];

  scheme_short_desc: string;
  scheme_description: string;
  scheme_eligibility: string;
  terms_and_condtions: string;

  sku_details: SkuDetail[];
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

export interface DropdownOption {
  id: number | string;
  name: string;
}

export const SCHEME_BASED_ON_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Region" },
  { id: 2, name: "State" },
  { id: 3, name: "City" },
  { id: 4, name: "Excel Upload" },
];

export const SCHEME_TYPE_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Value" },
  { id: 2, name: "Volume" },
];

export const TYPE_OF_CALCULATION_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Category" },
  { id: 2, name: "SKU" },
];

export const REWARD_TYPE_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Percentage" },
  { id: 2, name: "Multiplier" },
  { id: 3, name: "Flat Points" },
  { id: 4, name: "Spin Wheel" },
  { id: 5, name: "EGVs" },
  { id: 6, name: "Merchandise" },
];

export const SCHEME_TYPE_VARIANT_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Single" },
  { id: 2, name: "Multiple" },
  { id: 3, name: "Cumulative" },
];

export const TYPE_OF_BENEFIT_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Regular" },
  { id: 2, name: "Cash" },
];

export const SCHEME_TYPE_BASED_ON_OPTIONS: DropdownOption[] = [
  { id: 1, name: "Threshold" },
  { id: 2, name: "Slab Wise" },
];

export const THRESHOLD_POINTS_CALCULATION_OPTIONS: DropdownOption[] = [
  { id: 0, name: "All" },
  { id: 1, name: "After Threshold" },
];
